import React, { useEffect, useRef, useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Form,
  InputGroup,
  Button,
} from 'react-bootstrap';
import { get_open_issues, get_closed_issues } from '../../../utils/services';
import MiniStatusBar from './MiniStatusBar';
import HelpdeskDateRange from './HelpdeskDateRange';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function parseMySQLDateTime(mysqlString) {
  const [fecha, hora] = mysqlString.split(' ');
  const [ano, mes, dia] = fecha.split('-').map(Number);
  const [horas, minutos, segundos] = hora.split(':').map(Number);
  return new Date(ano, mes - 1, dia, horas, minutos, segundos);
}

function getElapsedHours(openfrom, dateLast) {
  if (dateLast == null) dateLast = openfrom;
  const fechaInicial = parseMySQLDateTime(dateLast);
  const fechaActual = new Date();
  const diferenciaMilisegundos = fechaActual.getTime() - fechaInicial.getTime();
  return parseInt(diferenciaMilisegundos / (1000 * 60 * 60), 10);
}

// Horas entre apertura y cierre (para casos cerrados). No usa NOW.
function getHoursBetweenDates(dateOpen, dateClose) {
  if (!dateOpen || !dateClose) return 0;
  const d1 = parseMySQLDateTime(dateOpen);
  const d2 = parseMySQLDateTime(dateClose);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
  const diffMs = d2.getTime() - d1.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60));
}

function Cases_report  ({user_id, permission}) {
    // --- Estado de filtros/orden/columnas ---
    const token = localStorage.getItem("token");
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'agehours', dir: 'desc' });
    const [initialRows, setinitialRows] = useState([]);
    const [casos_abiertos, setcasos_abiertos] = useState([]);
    const [casos_filterlist, setcasos_filterlist] = useState([]);
    const [activeTab, setActiveTab] = useState('open'); // 'open' o 'closed'
    const [isLoading, setIsLoading] = useState(false);
    const [closedDateRange, setClosedDateRange] = useState([{ startDate: null, endDate: null, key: 'selection' }]);
    const [isClosedDatePickerOpen, setIsClosedDatePickerOpen] = useState(false);
    const closedDatePickerRef = useRef(null);

    const navigate = useNavigate();   
    
    const getStatusText = (status) =>{

  switch (status){
    case 0:
      return ("Cerrado");
    case 1:
      return ("Abierto");
    case 2:
  
      return ("En observación");

  }
}
    // --- Filtrado + ordenamiento memoizados ---
    const rows = useMemo(() => {
      let result = initialRows;

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        result = result.filter((r) =>
          Object.values(r).some((v) => String(v).toLowerCase().includes(term))
        );
      }

      // Filtro por rango de fechas (solo en pestaña Cerrados): por lastInteraction (fecha de cierre)
      if (activeTab === 'closed' && closedDateRange[0]?.startDate && closedDateRange[0]?.endDate) {
        const startDate = new Date(closedDateRange[0].startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(closedDateRange[0].endDate);
        endDate.setHours(23, 59, 59, 999);
        result = result.filter((r) => {
          if (!r.lastInteraction) return false;
          const closedDate = parseMySQLDateTime(r.lastInteraction);
          if (isNaN(closedDate.getTime())) return false;
          return closedDate >= startDate && closedDate <= endDate;
        });
      }

      result = [...result].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === bVal) return 0;
        const order = aVal > bVal ? 1 : -1;
        return sortConfig.dir === 'asc' ? order : -order;
      });

      setcasos_filterlist(result.length);

      return result;
    }, [searchTerm, sortConfig, initialRows, activeTab, closedDateRange]);
  
    // --- Handlers ---
    const toggleSort = (key) => {
      setSortConfig((prev) => {
        if (prev.key === key) {
          return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        return { key, dir: 'asc' };
      });
    };

    const handleClosedDateChange = (item) => {
      if (item.selection) setClosedDateRange([item.selection]);
    };

    const clearClosedDateFilter = (e) => {
      e?.stopPropagation?.();
      setClosedDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
      setIsClosedDatePickerOpen(false);
    };

    // Casos cerrados: orden por defecto descendente por Cierre (lastInteraction)
    useEffect(() => {
      if (activeTab === 'closed') {
        setSortConfig({ key: 'lastInteraction', dir: 'desc' });
      }
    }, [activeTab]);

    // Datos para el gráfico de tiempo promedio (horas) por mes - solo en Cerrados
    const closedCasesChartData = useMemo(() => {
      if (activeTab !== 'closed' || !rows.length) return { labels: [], values: [] };
      const byMonth = {};
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      rows.forEach((r) => {
        if (!r.lastInteraction) return;
        const dateStr = r.lastInteraction.split(' ')[0];
        const [year, month] = dateStr.split('-');
        const monthKey = `${year}-${month}`;
        if (!byMonth[monthKey]) byMonth[monthKey] = [];
        byMonth[monthKey].push(typeof r.agehours === 'number' ? r.agehours : 0);
      });
      const sortedKeys = Object.keys(byMonth).sort();
      const labels = sortedKeys.map((k) => {
        const [y, m] = k.split('-');
        return `${monthNames[parseInt(m, 10) - 1]} ${y}`;
      });
      const values = sortedKeys.map((k) => {
        const arr = byMonth[k];
        const sum = arr.reduce((a, b) => a + b, 0);
        return Math.round((sum / arr.length) * 10) / 10;
      });
      return { labels, values };
    }, [activeTab, rows]);

  
    

    useEffect(() => {
      async function fetch_loadIssues(token, tab) {
        // Limpiar datos anteriores y mostrar loading
        setIsLoading(true);
        setinitialRows([]);
        setcasos_abiertos(0);
        setcasos_filterlist(0);
        
        try {
          const issueTypelist = tab === 'open' 
            ? await get_open_issues(token)
            : await get_closed_issues(token);
          
          if (issueTypelist==="INVALID"){
            setIsLoading(false);
            navigate("/");
            return;
          } 
          
          // Manejar caso de respuesta null o vacía
          if (!issueTypelist) {
            setcasos_abiertos(0);
            setinitialRows([]);
            setIsLoading(false);
            return;
          }
          
          // Verificar que tenga la estructura esperada
          if (issueTypelist["issues"] && Array.isArray(issueTypelist["issues"])) {
            const fullissues = issueTypelist["issues"].map((element, index) => {
                // Cerrados: agehours del backend (date→last_update). Puede venir como string desde JSON.
                // Fallback: getHoursBetweenDates para cerrados, getElapsedHours (date→NOW) para abiertos.
                const rawAgehours = element.agehours;
                const agehoursVal = (rawAgehours != null && rawAgehours !== '' && !isNaN(Number(rawAgehours)))
                  ? Number(rawAgehours)
                  : (element.last_update ? getHoursBetweenDates(element.date, element.last_update) : getElapsedHours(element.date, element.date));
                return { id: element.id, 
                          email: element.serial_num==""?element.email:element.serial_num,
                          openfrom: element.date,
                          CAS_status: element.CAS_status,
                          last_annotation: element.last_annotation,
                          lastInteraction: element.last_update,
                          agehours: agehoursVal,
                          status: parseInt(element.status),
                          source: element.serial_num==""?2:1,
                          index: index };
              });

            setcasos_abiertos(fullissues.length);
            setinitialRows(fullissues);
          } else {
            setcasos_abiertos(0);
            setinitialRows([]);
          }
        } catch (error) {
          console.error('Error fetching issues:', error);
          setcasos_abiertos(0);
          setinitialRows([]);
        } finally {
          setIsLoading(false);
        }
      }

      if (token) {
        fetch_loadIssues(token, activeTab);
      }
    }, [token, activeTab, navigate]); // Se ejecuta cuando cambia el token o la pestaña activa

    // Icono flecha ↑↓
    const SortIcon = ({ col }) =>
      sortConfig.key === col ? (sortConfig.dir === 'asc' ? '↑' : '↓') : '↕';


    const getAgeCellStyle = (agehours) => {
      // Manejar casos donde agehours no sea un número o sea negativo
      if (typeof agehours !== 'number' || agehours < 0) {
        return { backgroundColor: 'transparent' }; // Estilo por defecto para valores inválidos
      }
    
      if (agehours >= 60) {
        // 60 en adelante: rojo con texto blanco
        return { backgroundColor: 'red', color: 'white' };
      } else if (agehours >= 48) {
        // 48-59: naranja (originalmente "47-59", ajustado para evitar solapamiento)
        // El rango real cubierto es 48 <= agehours < 60
        return { backgroundColor: 'orange' };
      } else if (agehours >= 24) {
        // 24-47: amarillo
        // El rango real cubierto es 24 <= agehours < 48
        return { backgroundColor: 'yellow' };
      } else {
        // 0-23: transparente
        // El rango real cubierto es 0 <= agehours < 24
        return { backgroundColor: 'transparent' };
      }
    };

    // ---------- UI ----------
    return (
      <div className="container-fluid px-2 py-4">
      <h1 className="mb-4 fw-bold">Helpdesk Report TOMi8 y Plataforma</h1>
     
        {/* Pestañas de navegación */}
        <div className="d-flex gap-2 mb-4" style={{ borderBottom: '2px solid #dee2e6' }}>
          <button
            type="button"
            className={`btn ${activeTab === 'open' ? '' : 'btn-link'}`}
            onClick={() => {
              if (!isLoading) {
                setActiveTab('open');
              }
            }}
            disabled={isLoading}
            style={{
              fontWeight: activeTab === 'open' ? 'bold' : 'normal',
              backgroundColor: activeTab === 'open' ? '#0d6efd' : 'transparent',
              color: activeTab === 'open' ? 'white' : '#6c757d',
              border: 'none',
              borderBottom: activeTab === 'open' ? '3px solid #0d6efd' : '3px solid transparent',
              borderRadius: '0.375rem 0.375rem 0 0',
              padding: '0.5rem 1.5rem',
              marginBottom: '-2px',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'open' ? '0 2px 8px rgba(13, 110, 253, 0.3)' : 'none',
              fontSize: '1rem',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            🔴 Abiertos/Observación
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'closed' ? '' : 'btn-link'}`}
            onClick={() => {
              if (!isLoading) {
                setIsClosedDatePickerOpen(false);
                setActiveTab('closed');
              }
            }}
            disabled={isLoading}
            style={{
              fontWeight: activeTab === 'closed' ? 'bold' : 'normal',
              backgroundColor: activeTab === 'closed' ? '#198754' : 'transparent',
              color: activeTab === 'closed' ? 'white' : '#6c757d',
              border: 'none',
              borderBottom: activeTab === 'closed' ? '3px solid #198754' : '3px solid transparent',
              borderRadius: '0.375rem 0.375rem 0 0',
              padding: '0.5rem 1.5rem',
              marginBottom: '-2px',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'closed' ? '0 2px 8px rgba(25, 135, 84, 0.3)' : 'none',
              fontSize: '1rem',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            ✅ Cerrados
          </button>
        </div>

        {/* Barra de búsqueda + menús */}
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <InputGroup className="flex-grow-1">
              <Form.Control
                placeholder="Search all columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='toolbar-control'
              />
            </InputGroup>

            {/* Filtro por rango de fechas - solo en pestaña Cerrados */}
            {activeTab === 'closed' && (
              <div className="position-relative" ref={closedDatePickerRef}>
                <InputGroup onClick={() => setIsClosedDatePickerOpen((v) => !v)} style={{ cursor: 'pointer', minWidth: '220px' }}>
                  <Form.Control
                    readOnly
                    value={
                      closedDateRange[0].startDate && closedDateRange[0].endDate
                        ? `${closedDateRange[0].startDate.toLocaleDateString('es-ES')} - ${closedDateRange[0].endDate.toLocaleDateString('es-ES')}`
                        : 'Rango de fechas...'
                    }
                    className="toolbar-control"
                    style={{
                      cursor: 'pointer',
                      borderTopRightRadius: closedDateRange[0].startDate ? 0 : '0.375rem',
                      borderBottomRightRadius: closedDateRange[0].startDate ? 0 : '0.375rem',
                    }}
                  />
                  {closedDateRange[0].startDate && (
                    <Button
                      variant="light"
                      onClick={clearClosedDateFilter}
                      className="d-flex align-items-center border"
                      style={{ zIndex: 5 }}
                    >
                      <i className="bi bi-x-circle-fill" style={{ color: 'grey', fontSize: '1rem' }}></i>
                    </Button>
                  )}
                </InputGroup>
                {isClosedDatePickerOpen && (
                  <div className="position-absolute shadow-lg bg-white" style={{ zIndex: 1050, top: '100%', left: 0, marginTop: 4 }}>
                    <HelpdeskDateRange
                      range={closedDateRange}
                      handleDateChange={handleClosedDateChange}
                      setIsDatePickerOpen={setIsClosedDatePickerOpen}
                    />
                  </div>
                )}
              </div>
            )}
            
            <p className="mb-0 fw-bold"> 
              {activeTab === 'open' ? 'Casos abiertos' : 'Casos cerrados'} {casos_abiertos} filtrados: {casos_filterlist}
            </p>
          </div>

        {/* Gráfico de tiempo promedio (horas) - solo en Cerrados */}
        {activeTab === 'closed' && closedCasesChartData.labels.length > 0 && (
          <div className="mb-4 p-3 border rounded bg-light">
            <h6 className="mb-3 fw-bold">Tiempo promedio (horas) de casos cerrados por mes</h6>
            <div style={{ height: 280 }}>
              <Line
                data={{
                  labels: closedCasesChartData.labels,
                  datasets: [
                    {
                      label: 'Promedio de horas',
                      data: closedCasesChartData.values,
                      borderColor: 'rgb(25, 135, 84)',
                      backgroundColor: 'rgba(25, 135, 84, 0.1)',
                      tension: 0.3,
                      fill: true,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: true },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `Promedio: ${ctx.parsed.y} h`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: 'Horas' },
                    },
                    x: {
                      title: { display: true, text: 'Mes' },
                    },
                  },
                }}
              />
            </div>
          </div>
        )}

        {/* Tabla */}
        <Table hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              {activeTab === 'closed' && (
                <th onClick={() => toggleSort('id')} role="button">
                  ID <SortIcon col="id" />
                </th>
              )}
                <th onClick={() => toggleSort('email')} role="button">
                  Email del caso <SortIcon col="email" />
                </th>
                <th onClick={() => toggleSort('openfrom')} role="button">
                  Apertura <SortIcon col="openfrom" />
                </th>
              {activeTab === 'open' && (
                <th onClick={() => toggleSort('CAS_status')} role="button">
                  CAS status <SortIcon col="CAS_status" />
                </th>
              )}
              {activeTab === 'closed' && (
                <th onClick={() => toggleSort('lastInteraction')} role="button">
                  Cierre  <SortIcon col="lastInteraction" />
                </th>
              )}
                <th onClick={() => toggleSort('agehours')} role="button">
                  Horas <SortIcon col="agehours" />
                </th>
              {activeTab === 'open' && (
                <th>
                  Horas totales
                </th>
              )}
                <th onClick={() => toggleSort('type')} role="button">
                  Tipo <SortIcon col="type" />
                </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={activeTab === 'closed' ? 6 : 6} className="text-center py-5">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="text-muted mb-0">Cargando {activeTab === 'open' ? 'casos abiertos' : 'casos cerrados'}...</p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
             <tr key={row.id} onClick={() => {
              let targetUrl;
              if (row.source === 1) {
                  // Para issues de TOMi8, row.email es serial_num
                  targetUrl = `/helpdesk?serial=${row.email}`;
              } else if (row.source === 2) {
                  // Para issues de Platform, row.email es el email del usuario
                  targetUrl = `/helpdesk?email=${row.email}`;
              } 
             
              navigate(targetUrl);
              }}>
                {activeTab === 'closed' && <td>{row.id}</td>}
                <td>{row.email}</td>
                <td>{row.openfrom}</td>
                {activeTab === 'open' && (
                  <td>
                    {parseInt(row.CAS_status) != 0 && (
                      <MiniStatusBar
                        currentStep={parseInt(row.CAS_status)}
                        totalSteps={8}
                      />
                    )}
                  </td>
                )}
                {activeTab === 'closed' && <td>{row.lastInteraction || '-'}</td>}
                <td style={getAgeCellStyle((row.agehours))}>{row.agehours}</td>
                {activeTab === 'open' && <td>{getElapsedHours(row.openfrom, row.openfrom)}</td>}
                <td>
                  <span className={`badge ${row.status === 0 ? 'bg-success' : (row.status === 1 ? 'bg-danger' : 'bg-primary')}`}>
                    {getStatusText(row.status)}
                  </span>
                </td>
              </tr>
              ))
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={activeTab === 'closed' ? 6 : 6} className="text-center py-4">
                  No hay resultados
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
};

export default Cases_report;
